import { Position, type Node } from '@xyflow/react';
import { GRAPH_NODE_CIRCLE_SIZE, type AssetNodeData } from './constants';

export function getNodeBounds(node: Node<AssetNodeData>) {
  const width = node.measured?.width ?? node.width ?? 180;

  return {
    centerX: node.position.x + width / 2,
    centerY: node.position.y + GRAPH_NODE_CIRCLE_SIZE / 2,
    width,
    height: GRAPH_NODE_CIRCLE_SIZE,
  };
}

export function getNearestNodeSide({
  fromX,
  fromY,
  toX,
  toY,
}: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}) {
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX > 0 ? Position.Right : Position.Left;
  }

  return deltaY > 0 ? Position.Bottom : Position.Top;
}

const GRAPH_NODE_CIRCLE_RADIUS = GRAPH_NODE_CIRCLE_SIZE / 2;

export function getNodeConnectionPoint({
  centerX,
  centerY,
  side,
}: {
  centerX: number;
  centerY: number;
  side: Position;
}) {
  if (side === Position.Left) {
    return { x: centerX - GRAPH_NODE_CIRCLE_RADIUS, y: centerY };
  }

  if (side === Position.Right) {
    return { x: centerX + GRAPH_NODE_CIRCLE_RADIUS, y: centerY };
  }

  if (side === Position.Top) {
    return { x: centerX, y: centerY - GRAPH_NODE_CIRCLE_RADIUS };
  }

  return { x: centerX, y: centerY + GRAPH_NODE_CIRCLE_RADIUS };
}

export function getEdgeCoordinates({
  sourceNode,
  targetNode,
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceNode: Node<AssetNodeData> | undefined;
  targetNode: Node<AssetNodeData> | undefined;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}) {
  if (!sourceNode || !targetNode) {
    return {
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  }

  const sourceBounds = getNodeBounds(sourceNode);
  const targetBounds = getNodeBounds(targetNode);
  const sourcePosition = getNearestNodeSide({
    fromX: sourceBounds.centerX,
    fromY: sourceBounds.centerY,
    toX: targetBounds.centerX,
    toY: targetBounds.centerY,
  });
  const targetPosition = getNearestNodeSide({
    fromX: targetBounds.centerX,
    fromY: targetBounds.centerY,
    toX: sourceBounds.centerX,
    toY: sourceBounds.centerY,
  });
  const sourcePoint = getNodeConnectionPoint({
    centerX: sourceBounds.centerX,
    centerY: sourceBounds.centerY,
    side: sourcePosition,
  });
  const targetPoint = getNodeConnectionPoint({
    centerX: targetBounds.centerX,
    centerY: targetBounds.centerY,
    side: targetPosition,
  });

  return {
    sourceX: sourcePoint.x,
    sourceY: sourcePoint.y,
    targetX: targetPoint.x,
    targetY: targetPoint.y,
    sourcePosition,
    targetPosition,
  };
}
